import { useEffect, useState } from 'react'
import { Building2, Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCompany, useUpdateCompany } from '@/api/company'
import { useProfile } from '@/api/profile'

/**
 * Réglage du nom de l'entreprise, affiché dans la page Profil pour les
 * MANAGERS uniquement. Précédemment portée par CompanyView (sidebar
 * "Entreprise") — déplacée ici après la migration global → per-number qui a
 * vidé la page Company de son contenu.
 */
export function CompanyNameSection({ darkMode }: { darkMode: boolean }) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const isManager = profile?.role === 'MANAGER'
  const companyId = profile?.company

  const { data: company, isLoading } = useCompany(
    isManager ? companyId : undefined,
  )
  const updateCompany = useUpdateCompany(companyId)

  const [value, setValue] = useState<string>('')

  useEffect(() => {
    if (company?.name) setValue(company.name)
  }, [company?.name])

  if (!isManager || !companyId) return null

  const trimmed = value.trim()
  const isDirty = trimmed !== (company?.name ?? '') && trimmed.length > 0

  const handleSave = async () => {
    try {
      await updateCompany.mutateAsync({ name: trimmed })
      toast.success(
        t('profile.companyNameSaved', 'Nom de l\'entreprise mis à jour'),
      )
    } catch {
      toast.error(
        t('profile.errors.companyNameSave', 'Erreur lors de la sauvegarde'),
      )
    }
  }

  return (
    <section
      className={cn(
        'rounded-xl border p-5',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-indigo-500" />
        <h2
          className={cn(
            'text-sm font-semibold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t('profile.sections.companyName', "Nom de l'entreprise")}
        </h2>
      </div>
      <p
        className={cn(
          'text-xs mb-4',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t(
          'profile.companyNameHint',
          "Affiché dans le tableau de bord et utilisé sur la facturation.",
        )}
      </p>

      {isLoading ? (
        <div className="py-4 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5 max-w-md">
            <Label className="text-xs font-medium">
              {t('profile.companyNameLabel', 'Nom')}
            </Label>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('profile.companyNamePlaceholder', "Acme inc.")}
            />
          </div>

          {isDirty && (
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setValue(company?.name ?? '')}
                disabled={updateCompany.isPending}
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleSave()}
                disabled={updateCompany.isPending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {updateCompany.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('profile.save', 'Sauvegarder')}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
